/**
 * Input masks and date helpers for the application wizard.
 *
 * Storage conventions (what we send to the API):
 *   full dates  -> 'YYYY-MM-DD' (from native <input type="date">)
 *   months      -> 'MM/YYYY'    (masked text input; native type="month" is not
 *                                supported on desktop Safari/Firefox)
 *   phone       -> digits only, normalised server-side to +1XXXXXXXXXX
 */

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

export const formatMonthInput = (raw) => {
  const digits = digitsOnly(raw).slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const formatUsDateInput = (raw) => {
  const digits = digitsOnly(raw).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const formatPhoneInput = (raw) => {
  let digits = digitsOnly(raw);
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (!digits.length) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatSsnInput = (raw) => {
  const digits = digitsOnly(raw).slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export const formatZipInput = (raw) => {
  const digits = digitsOnly(raw).slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const phoneDigits = (value) => {
  let digits = digitsOnly(value);
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits;
};

export const ssnDigits = (value) => digitsOnly(value).slice(0, 9);

export const isValidMonth = (value) => /^(0[1-9]|1[0-2])\/\d{4}$/.test(String(value || ''));

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "05/2021" -> "May 2021"; anything else is returned unchanged. */
export const formatMonthLabel = (value) => (
  isValidMonth(value) ? `${MONTH_NAMES[Number(value.slice(0, 2)) - 1]} ${value.slice(3)}` : value
);

export const isValidIsoDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return false;
  const [y, m, d] = String(value).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
};

/** 'YYYY-MM-DD' -> local Date (no timezone shift). */
export const parseIsoDate = (value) => {
  if (!isValidIsoDate(value)) return null;
  const [y, m, d] = String(value).split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const todayIso = () => toIsoDate(new Date());

/** 'YYYY-MM-DD' -> 'MM/DD/YYYY' for display. */
export const isoToUsDate = (value) => {
  if (!isValidIsoDate(value)) return value || '';
  const [y, m, d] = String(value).split('-');
  return `${m}/${d}/${y}`;
};

/** 'MM/DD/YYYY' -> 'YYYY-MM-DD' (returns '' when invalid). */
export const usDateToIso = (value) => {
  const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '';
  const iso = `${match[3]}-${match[1]}-${match[2]}`;
  return isValidIsoDate(iso) ? iso : '';
};

export const ageOn = (dobIso, now = new Date()) => {
  const dob = parseIsoDate(dobIso);
  if (!dob) return null;
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthday = now.getMonth() < dob.getMonth()
    || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

export const formatDisplayDate = (value) => {
  const date = typeof value === 'string' ? parseIsoDate(value) : value;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatDisplayDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Apply a mask inside an onChange handler without breaking react-hook-form's uncontrolled flow. */
export const withMask = (formatter, onChange) => (event) => {
  const formatted = formatter(event.target.value);
  if (formatted !== event.target.value) {
    event.target.value = formatted;
  }
  if (typeof onChange === 'function') {
    onChange(event);
  }
};
